import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  AdvancedThumbnailOptimizer,
  ConnectionOptimizer,
  PredictiveLoader,
  VideoCache,
  initializeAdvancedVideoOptimization,
} from "../utils/advanced-video-optimization";
import { VideoPerformanceTracker } from "../utils/video-performance-analytics";

interface EnhancedYouTubeEmbedProps {
  videoId: string;
  title?: string;
  className?: string;
  aspectRatio?: "16:9" | "4:3" | "1:1";
  lazyLoad?: boolean;
  showThumbnail?: boolean;
  autoplay?: boolean;
  muted?: boolean;
  enableAdvancedOptimizations?: boolean;
  onLoadStart?: () => void;
  onLoadComplete?: () => void;
  onError?: (error: Error) => void;
}

// Helper function to extract video ID from various YouTube URL formats
export function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;

  // Handle youtu.be format
  if (url.includes("youtu.be/")) {
    const id = url.split("youtu.be/")[1]?.split(/[?&#]/)[0];
    return id || null;
  }

  // Handle youtube.com/watch?v= format
  const watchMatch = url.match(
    /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/\?v=)([^?&#]+)/,
  );
  if (watchMatch) {
    return watchMatch[1] || null;
  }

  // If the input is already just a video ID (11 characters)
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
    return url;
  }

  return null;
}

export default function EnhancedYouTubeEmbed({
  videoId,
  title = "YouTube video player",
  className = "",
  aspectRatio = "16:9",
  lazyLoad = true,
  showThumbnail = true,
  autoplay = false,
  muted = false,
  enableAdvancedOptimizations = true,
  onLoadStart,
  onLoadComplete,
  onError,
}: EnhancedYouTubeEmbedProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(!lazyLoad);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadStartTime, setLoadStartTime] = useState<number>(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const optimizationInitialized = useRef(false);
  const performanceTracker = useRef<VideoPerformanceTracker | null>(null);

  // Ensure we have a clean video ID
  const cleanVideoId = extractYouTubeVideoId(videoId) || videoId;

  // Initialize advanced optimizations and performance tracking
  useEffect(() => {
    if (enableAdvancedOptimizations && !optimizationInitialized.current) {
      initializeAdvancedVideoOptimization().then(() => {
        optimizationInitialized.current = true;
        console.log(
          "🎬 Advanced video optimization initialized for",
          cleanVideoId,
        );
      });
    }

    // Initialize performance tracker
    if (!performanceTracker.current) {
      performanceTracker.current = new VideoPerformanceTracker(cleanVideoId);
    }

    // Add preconnect hints for YouTube
    if (typeof document !== "undefined") {
      const preconnectLink = document.createElement("link");
      preconnectLink.rel = "preconnect";
      preconnectLink.href = "https://www.youtube.com";
      document.head.appendChild(preconnectLink);

      const preconnectLink2 = document.createElement("link");
      preconnectLink2.rel = "preconnect";
      preconnectLink2.href = "https://www.youtube-nocookie.com";
      document.head.appendChild(preconnectLink2);
    }
  }, [enableAdvancedOptimizations, cleanVideoId]);

  // Calculate padding based on aspect ratio
  const getPaddingBottom = () => {
    switch (aspectRatio) {
      case "4:3":
        return "75%";
      case "1:1":
        return "100%";
      case "16:9":
      default:
        return "56.25%";
    }
  };

  // Get optimal settings based on connection
  const getOptimalSettings = useCallback(() => {
    if (enableAdvancedOptimizations) {
      const connectionOptimizer = ConnectionOptimizer.getInstance();
      return connectionOptimizer.getOptimalSettings();
    }
    return {
      quality: "hq",
      preload: true,
      lazyLoad: false,
      autoplay: false,
      muted: false,
      enablePredictiveLoading: false,
    };
  }, [enableAdvancedOptimizations]);

  // Intersection Observer for lazy loading with advanced settings
  useEffect(() => {
    if (!lazyLoad || !containerRef.current) {
      setIsVisible(true);
      return;
    }

    const settings = getOptimalSettings();
    const rootMargin = settings.enablePredictiveLoading ? "100px" : "50px";

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin,
        threshold: 0.1,
      },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [lazyLoad, getOptimalSettings]);

  // Advanced thumbnail loading with fallback strategy
  useEffect(() => {
    if (
      isVisible &&
      showThumbnail &&
      cleanVideoId &&
      enableAdvancedOptimizations
    ) {
      const loadThumbnail = async () => {
        try {
          // Start performance tracking
          performanceTracker.current?.startThumbnailLoading();

          const thumbnailOptimizer = AdvancedThumbnailOptimizer.getInstance();
          const settings = getOptimalSettings();

          const url = await thumbnailOptimizer.getOptimizedThumbnail(
            cleanVideoId,
            settings.quality as any,
          );

          setThumbnailUrl(url);
          setThumbnailLoaded(true);

          // Record thumbnail load time
          performanceTracker.current?.thumbnailLoaded();

          // Record interaction for predictive loading
          const predictiveLoader = PredictiveLoader.getInstance();
          predictiveLoader.recordInteraction(cleanVideoId);
        } catch (error) {
          console.warn("Failed to load optimized thumbnail:", error);
          setThumbnailError(true);
          performanceTracker.current?.recordError(
            `Thumbnail load failed: ${error}`,
          );
        }
      };

      loadThumbnail();
    } else if (isVisible && showThumbnail && cleanVideoId) {
      // Fallback to basic thumbnail loading
      console.log(
        "🔍 Debug: Starting basic thumbnail loading for",
        cleanVideoId,
      );
      performanceTracker.current?.startThumbnailLoading();

      const img = new Image();
      img.onload = () => {
        console.log(
          "✅ Debug: Thumbnail loaded successfully for",
          cleanVideoId,
        );
        setThumbnailUrl(
          `https://img.youtube.com/vi/${cleanVideoId}/hqdefault.jpg`,
        );
        setThumbnailLoaded(true);
        console.log("🔄 Debug: Setting thumbnailLoaded to true");
        performanceTracker.current?.thumbnailLoaded();
      };
      img.onerror = () => {
        console.error("❌ Debug: Thumbnail load failed for", cleanVideoId);
        setThumbnailError(true);
        performanceTracker.current?.recordError("Basic thumbnail load failed");
      };
      img.src = `https://img.youtube.com/vi/${cleanVideoId}/hqdefault.jpg`;
    }
  }, [isVisible, showThumbnail, cleanVideoId, enableAdvancedOptimizations]);

  // Debug thumbnail state changes
  useEffect(() => {
    console.log(
      "🔍 Debug: Thumbnail state changed - loaded:",
      thumbnailLoaded,
      "error:",
      thumbnailError,
      "url:",
      thumbnailUrl ? "set" : "empty",
    );
  }, [thumbnailLoaded, thumbnailError, thumbnailUrl]);

  // Build optimized YouTube embed URL
  const buildEmbedUrl = useCallback(() => {
    const settings = getOptimalSettings();

    const params = new URLSearchParams({
      rel: "0",
      modestbranding: "1",
      showinfo: "0",
      iv_load_policy: "3",
      cc_load_policy: "0",
      fs: "1",
      enablejsapi: "1",
      origin: window.location.origin,
      // Add performance optimizations
      autohide: "1",
      color: "white",
      controls: "1",
      disablekb: "0",
      loop: "0",
      playlist: cleanVideoId,
    });

    // Only autoplay if explicitly requested via props (respect user preference)
    if (autoplay) {
      params.append("autoplay", "1");
      if (muted) {
        params.append("mute", "1");
      }
    }

    return `https://www.youtube.com/embed/${cleanVideoId}?${params.toString()}`;
  }, [cleanVideoId, autoplay, muted]);

  // Handle iframe load with performance tracking
  const handleIframeLoad = useCallback(() => {
    const iframeLoadTime = performance.now() - loadStartTime;
    setIsLoaded(true);
    setIsLoading(false);

    console.log(
      `📹 Video ${cleanVideoId} loaded in ${iframeLoadTime.toFixed(2)}ms`,
    );

    // Cache video data
    if (enableAdvancedOptimizations) {
      const videoCache = VideoCache.getInstance();
      videoCache.set(`video_${cleanVideoId}`, {
        loadTime: iframeLoadTime,
        thumbnailUrl,
        loadedAt: Date.now(),
      });
    }

    // Complete performance tracking
    if (performanceTracker.current) {
      const settings = getOptimalSettings();
      performanceTracker.current.complete(
        performanceTracker.current.thumbnailLoaded(),
        iframeLoadTime,
        {
          thumbnailQuality: settings.quality,
          cacheHit: false, // TODO: Implement cache hit detection
          optimizationEnabled: enableAdvancedOptimizations,
        },
      );
    }

    onLoadComplete?.();
  }, [
    cleanVideoId,
    loadStartTime,
    thumbnailUrl,
    enableAdvancedOptimizations,
    onLoadComplete,
  ]);

  // Handle iframe load start
  const handleIframeLoadStart = useCallback(() => {
    setLoadStartTime(performance.now());
    setIsLoading(true);
    performanceTracker.current?.startIframeLoading();
    onLoadStart?.();
  }, [onLoadStart]);

  // Enhanced load start tracking - trigger when iframe becomes visible
  useEffect(() => {
    if (isVisible && !isLoaded && !isLoading) {
      // Set load start time when iframe becomes visible
      setLoadStartTime(performance.now());
      setIsLoading(true);
      performanceTracker.current?.startIframeLoading();
      onLoadStart?.();
    }
  }, [isVisible, isLoaded, isLoading, onLoadStart]);

  // Start performance monitoring
  useEffect(() => {
    if (containerRef.current && enableAdvancedOptimizations) {
      // Record video interaction for predictive loading
      const predictiveLoader = PredictiveLoader.getInstance();
      predictiveLoader.recordInteraction(cleanVideoId);

      // Preload predicted videos in background
      setTimeout(() => {
        const allVideoIds = ["WjHWcaLFgeI"]; // Add more video IDs as needed
        predictiveLoader.preloadPredictedVideos(cleanVideoId, allVideoIds);
      }, 1000);
    }
  }, [cleanVideoId, enableAdvancedOptimizations]);

  // Error handling
  const handleError = useCallback(
    (error: Error) => {
      console.error(`Error loading video ${cleanVideoId}:`, error);
      onError?.(error);
    },
    [cleanVideoId, onError],
  );

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${className}`}
      style={{ paddingBottom: getPaddingBottom() }}
    >
      {/* Advanced thumbnail placeholder */}
      {showThumbnail && isVisible && !isLoaded && (
        <div className="absolute top-0 left-0 w-full h-full rounded-lg shadow-md overflow-hidden bg-gray-100">
          {thumbnailLoaded && !thumbnailError && thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={title}
              className="w-full h-full object-cover"
              loading="eager"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <div className="text-gray-400">
                <svg
                  className="w-12 h-12 mx-auto mb-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
                <p className="text-sm">
                  {enableAdvancedOptimizations
                    ? "Optimizing video..."
                    : "Loading video..."}
                </p>
              </div>
            </div>
          )}

          {/* Enhanced play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-black bg-opacity-50 rounded-full p-4 hover:bg-opacity-70 transition-all duration-200 cursor-pointer group">
              <svg
                className="w-8 h-8 text-white group-hover:scale-110 transition-transform duration-200"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>

          {/* Loading indicator */}
          {isLoading && (
            <div className="absolute top-4 right-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
            </div>
          )}
        </div>
      )}

      {/* Enhanced YouTube iframe */}
      {isVisible && (
        <iframe
          ref={iframeRef}
          className={`absolute top-0 left-0 w-full h-full rounded-lg shadow-md transition-opacity duration-300 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          src={buildEmbedUrl()}
          title={title}
          loading={lazyLoad ? "lazy" : "eager"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          onLoad={handleIframeLoad}
          onLoadStart={handleIframeLoadStart}
          onError={() => handleError(new Error("Failed to load iframe"))}
        />
      )}

      {/* Enhanced loading indicator - only show if thumbnail not loaded */}
      {isVisible && !isLoaded && !thumbnailLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">
              {enableAdvancedOptimizations
                ? "Loading optimized video..."
                : "Loading video..."}
            </p>
          </div>
        </div>
      )}

      {/* Performance indicator (development only) */}
      {process.env.NODE_ENV === "development" &&
        enableAdvancedOptimizations && (
          <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded opacity-75">
            Advanced
          </div>
        )}
    </div>
  );
}
